import { useState, DragEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Table,
  Button,
  Switch,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  message,
  Space,
  Card,
  Tabs,
  Divider,
  Row,
  Col,
  Typography,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  HolderOutlined,
  DatabaseOutlined,
  ShareAltOutlined,
  HistoryOutlined,
  MessageOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  ToolOutlined,
  SettingOutlined,
  ExperimentOutlined,
  AppstoreOutlined,
  RobotOutlined,
  ThunderboltOutlined,
  LineChartOutlined,
  BarChartOutlined,
  FileOutlined,
  CommentOutlined,
} from '@ant-design/icons'
import appConfigApi, { AppConfiguration, CreateAppConfigurationRequest } from '../lib/appConfigApi'
import FeatureFlagsSettings from './FeatureFlagsSettings'

const { TabPane } = Tabs
const { Option } = Select
const { Text } = Typography

// Icon mapping for sidebar items
const iconMap: Record<string, any> = {
  DatabaseOutlined,
  ShareAltOutlined,
  HistoryOutlined,
  MessageOutlined,
  ToolOutlined,
  SettingOutlined,
  ExperimentOutlined,
  AppstoreOutlined,
  RobotOutlined,
  ThunderboltOutlined,
  LineChartOutlined,
  BarChartOutlined,
  FileOutlined,
  CommentOutlined,
}

const getIconComponent = (iconName: string) => {
  const IconComponent = iconMap[iconName]
  return IconComponent ? <IconComponent /> : <AppstoreOutlined />
}

interface AppConfigurationSettingsProps {
  applicationFilter?: string
  defaultApplication?: string
}

const AppConfigurationSettings = ({
  applicationFilter = 'app',
  defaultApplication = 'app'
}: AppConfigurationSettingsProps = {}) => {
  const [activeTab, setActiveTab] = useState('sidebar')
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [editingConfig, setEditingConfig] = useState<AppConfiguration | null>(null)
  const [draggedItem, setDraggedItem] = useState<string | null>(null)
  const [dragOverItem, setDragOverItem] = useState<string | null>(null)
  const [form] = Form.useForm()
  const queryClient = useQueryClient()

  // Queries - filter by application
  const { data: configurations, isLoading } = useQuery({
    queryKey: ['app-configurations', applicationFilter],
    queryFn: () => appConfigApi.getConfigurationsByApplication(applicationFilter),
  })


  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateAppConfigurationRequest) => appConfigApi.createConfiguration(data),
    onSuccess: () => {
      message.success('Configuration created successfully')
      queryClient.invalidateQueries({ queryKey: ['app-configurations'] })
      queryClient.invalidateQueries({ queryKey: ['sidebar-configuration'] })
      queryClient.invalidateQueries({ queryKey: ['training-sidebar-configuration'] })
      setIsModalVisible(false)
      form.resetFields()
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to create configuration')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => appConfigApi.updateConfiguration(id, data),
    onSuccess: () => {
      message.success('Configuration updated successfully')
      queryClient.invalidateQueries({ queryKey: ['app-configurations'] })
      queryClient.invalidateQueries({ queryKey: ['sidebar-configuration'] })
      queryClient.invalidateQueries({ queryKey: ['training-sidebar-configuration'] })
      setIsModalVisible(false)
      setEditingConfig(null)
      form.resetFields()
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to update configuration')
    },
  })

  const updateSortOrderMutation = useMutation({
    mutationFn: ({ updates }: { updates: { id: string; sortOrder: number }[] }) =>
      Promise.all(updates.map(update =>
        appConfigApi.updateConfiguration(update.id, { sortOrder: update.sortOrder })
      )),
    onSuccess: () => {
      message.success('Order updated successfully')
      queryClient.invalidateQueries({ queryKey: ['app-configurations'] })
      queryClient.invalidateQueries({ queryKey: ['sidebar-configuration'] })
      queryClient.invalidateQueries({ queryKey: ['training-sidebar-configuration'] })
    },
    onError: () => {
      message.error('Failed to update order')
    },
  })

  const toggleMutation = useMutation({
    mutationFn: ({ id, isEnabled }: { id: string; isEnabled: boolean }) =>
      appConfigApi.toggleConfiguration(id, isEnabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-configurations'] })
      queryClient.invalidateQueries({ queryKey: ['sidebar-configuration'] })
      queryClient.invalidateQueries({ queryKey: ['training-sidebar-configuration'] })
    },
  })

  const toggleVisibilityMutation = useMutation({
    mutationFn: ({ id, isVisible }: { id: string; isVisible: boolean }) =>
      appConfigApi.toggleVisibility(id, isVisible),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-configurations'] })
      queryClient.invalidateQueries({ queryKey: ['sidebar-configuration'] })
      queryClient.invalidateQueries({ queryKey: ['training-sidebar-configuration'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => appConfigApi.deleteConfiguration(id),
    onSuccess: () => {
      message.success('Configuration deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['app-configurations'] })
      queryClient.invalidateQueries({ queryKey: ['sidebar-configuration'] })
      queryClient.invalidateQueries({ queryKey: ['training-sidebar-configuration'] })
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to delete configuration')
    },
  })


  const initializeDefaultsMutation = useMutation({
    mutationFn: () => {
      // Use training-specific initialization if in training app
      if (applicationFilter === 'training') {
        return appConfigApi.initializeTrainingDefaults()
      }
      return appConfigApi.initializeDefaults()
    },
    onSuccess: () => {
      const appName = applicationFilter === 'training' ? 'Training' : 'Main'
      message.success(`${appName} app default configurations initialized`)
      queryClient.invalidateQueries({ queryKey: ['app-configurations'] })
      queryClient.invalidateQueries({ queryKey: ['sidebar-configuration'] })
      queryClient.invalidateQueries({ queryKey: ['training-sidebar-configuration'] })
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || 'Failed to initialize defaults')
    },
  })

  const handleCreate = () => {
    setEditingConfig(null)
    setIsModalVisible(true)
    form.resetFields()
    form.setFieldsValue({
      application: defaultApplication,
      type: activeTab,
      isEnabled: true,
      isVisible: true,
      sortOrder: 0
    })
  }

  const handleEdit = (config: AppConfiguration) => {
    setEditingConfig(config)
    setIsModalVisible(true)
    form.setFieldsValue(config)
  }

  const handleDelete = (id: string) => {
    Modal.confirm({
      title: 'Delete Configuration',
      content: 'Are you sure you want to delete this configuration?',
      okText: 'Yes, Delete',
      okType: 'danger',
      onOk: () => deleteMutation.mutate(id),
    })
  }

  const handleSubmit = async (values: any) => {
    try {
      if (editingConfig) {
        await updateMutation.mutateAsync({ id: editingConfig.id, data: values })
      } else {
        await createMutation.mutateAsync(values)
      }
    } catch (error) {
      // Error is handled in mutation
    }
  }

  const handleDragStart = (e: DragEvent<HTMLDivElement>, id: string) => {
    setDraggedItem(id)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/html', id)
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDragEnter = (e: DragEvent<HTMLDivElement>, id: string) => {
    e.preventDefault()
    setDragOverItem(id)
  }

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragOverItem(null)
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>, targetId: string) => {
    e.preventDefault()
    
    if (!draggedItem || draggedItem === targetId) {
      setDraggedItem(null)
      setDragOverItem(null)
      return
    }

    const configs = getConfigurationsByType('sidebar').sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
    const draggedIndex = configs.findIndex(c => c.id === draggedItem)
    const targetIndex = configs.findIndex(c => c.id === targetId)

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedItem(null)
      setDragOverItem(null)
      return
    }

    // Create new order based on drag and drop
    const newConfigs = [...configs]
    const [removed] = newConfigs.splice(draggedIndex, 1)
    newConfigs.splice(targetIndex, 0, removed)

    // Update sort orders
    const updates = newConfigs.map((config, index) => ({
      id: config.id,
      sortOrder: index
    }))

    updateSortOrderMutation.mutate({ updates })
    setDraggedItem(null)
    setDragOverItem(null)
  }

  const handleDragEnd = () => {
    setDraggedItem(null)
    setDragOverItem(null)
  }


  const getConfigurationsByType = (type: string) => {
    if (!configurations) return []
    return configurations.filter(config => config.type === type)
  }


  const renderSidebarCards = () => {
    const configs = getConfigurationsByType('sidebar').sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
    
    if (configs.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <Text type="secondary">No sidebar configurations found</Text>
        </div>
      )
    }

    return (
      <div style={{ padding: '8px 0' }}>
        <Row gutter={[0, 8]}>
          {configs.map((config) => (
            <Col span={24} key={config.id}>
              <Card
                size="small"
                draggable
                onDragStart={(e) => handleDragStart(e, config.id)}
                onDragOver={handleDragOver}
                onDragEnter={(e) => handleDragEnter(e, config.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, config.id)}
                onDragEnd={handleDragEnd}
                style={{
                  cursor: draggedItem === config.id ? 'grabbing' : 'grab',
                  border: dragOverItem === config.id 
                    ? '2px solid #1890ff' 
                    : config.isEnabled ? '1px solid #d9d9d9' : '1px solid #f0f0f0',
                  backgroundColor: config.isEnabled ? '#ffffff' : '#fafafa',
                  opacity: draggedItem === config.id ? 0.5 : config.isEnabled ? 1 : 0.7,
                  transform: draggedItem === config.id ? 'scale(0.98)' : 'scale(1)',
                  transition: 'all 0.2s ease',
                  minHeight: 'auto',
                }}
                bodyStyle={{ padding: '8px 12px' }}
              >
                <Row align="middle" justify="space-between">
                  {/* Icon & Drag Handle */}
                  <Col flex="none" style={{ width: '60px' }}>
                    <Space align="center" size={8}>
                      <div style={{ fontSize: '16px', color: '#1890ff' }}>
                        {getIconComponent(config.icon || 'AppstoreOutlined')}
                      </div>
                      <HolderOutlined style={{ color: '#bfbfbf', cursor: 'grab', fontSize: '12px' }} />
                    </Space>
                  </Col>

                  {/* Content */}
                  <Col flex="auto">
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Text strong style={{ fontSize: '14px', lineHeight: '20px' }}>
                          {config.name}
                        </Text>
                        {config.featureFlag && (
                          <span style={{
                            background: '#f0f2ff',
                            border: '1px solid #d6e4ff',
                            borderRadius: '4px',
                            padding: '1px 6px',
                            fontSize: '10px',
                            color: '#1890ff',
                            fontFamily: 'monospace'
                          }}>
                            {config.featureFlag}
                          </span>
                        )}
                      </div>
                      {config.description && (
                        <div style={{ marginTop: '2px' }}>
                          <Text type="secondary" style={{ fontSize: '11px', lineHeight: '16px' }}>
                            {config.description}
                          </Text>
                        </div>
                      )}
                      {config.route && (
                        <div style={{ marginTop: '2px' }}>
                          <Text code style={{ fontSize: '10px', color: '#666', background: '#f5f5f5' }}>
                            {config.route}
                          </Text>
                        </div>
                      )}
                    </div>
                  </Col>

                  {/* Order Badge */}
                  <Col flex="none" style={{ width: '40px', textAlign: 'center' }}>
                    <div style={{ 
                      background: '#f0f0f0', 
                      borderRadius: '4px', 
                      padding: '2px 6px',
                      fontSize: '10px',
                      color: '#666'
                    }}>
                      #{config.sortOrder || 0}
                    </div>
                  </Col>

                  {/* Controls */}
                  <Col flex="none" style={{ width: '80px' }}>
                    <Space size={4} align="center">
                      {/* Enable/Disable Toggle */}
                      <Switch
                        size="small"
                        checked={config.isEnabled}
                        onChange={(checked) => {
                          // Update both enabled and visible states together
                          toggleMutation.mutate({ id: config.id, isEnabled: checked })
                          if (checked !== config.isVisible) {
                            toggleVisibilityMutation.mutate({ id: config.id, isVisible: checked })
                          }
                        }}
                        loading={toggleMutation.isPending || toggleVisibilityMutation.isPending}
                      />

                      {/* Edit Button */}
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(config)}
                        style={{ width: '24px', height: '24px', padding: 0 }}
                      />
                      
                      {/* Delete Button */}
                      <Button
                        type="text"
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(config.id)}
                        danger
                        style={{ width: '24px', height: '24px', padding: 0 }}
                      />
                    </Space>
                  </Col>
                </Row>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    )
  }

  const renderTableContent = (type: string) => {
    const configs = getConfigurationsByType(type).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
    
    const columns = [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        render: (name: string, record: AppConfiguration) => (
          <div>
            <div style={{ fontWeight: 500, fontSize: '14px', marginBottom: '4px' }}>
              {name}
            </div>
            {record.description && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {record.description}
              </Text>
            )}
          </div>
        ),
      },
      {
        title: 'Key',
        dataIndex: 'key',
        key: 'key',
        width: 150,
        render: (key: string) => <Text code>{key}</Text>,
      },
      {
        title: 'Route',
        dataIndex: 'route',
        key: 'route',
        width: 120,
        render: (route: string) => route ? <Text code>{route}</Text> : '-',
      },
      {
        title: 'Order',
        dataIndex: 'sortOrder',
        key: 'sortOrder',
        width: 80,
        render: (order: number) => <Text>{order}</Text>,
      },
      {
        title: 'Status',
        key: 'status',
        width: 120,
        render: (_: any, record: AppConfiguration) => (
          <Space>
            <Switch
              size="small"
              checked={record.isEnabled}
              onChange={(checked) => toggleMutation.mutate({ id: record.id, isEnabled: checked })}
              loading={toggleMutation.isPending}
            />
            <Button
              type="text"
              size="small"
              icon={record.isVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
              onClick={() => toggleVisibilityMutation.mutate({ id: record.id, isVisible: !record.isVisible })}
              loading={toggleVisibilityMutation.isPending}
            />
          </Space>
        ),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 80,
        render: (_: any, record: AppConfiguration) => (
          <Space>
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
            <Button
              type="text"
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.id)}
              danger
            />
          </Space>
        ),
      },
    ]

    return (
      <Table
        columns={columns}
        dataSource={configs}
        rowKey="id"
        loading={isLoading}
        pagination={false}
        locale={{ emptyText: `No ${type} configurations found` }}
        scroll={{ y: 400 }}
        size="small"
      />
    )
  }

  const renderTypeContent = (type: string) => {
    if (type === 'sidebar') {
      return renderSidebarCards()
    }
    return renderTableContent(type)
  }


  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => initializeDefaultsMutation.mutate()}
            loading={initializeDefaultsMutation.isPending}
          >
            Initialize Defaults
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            Add Configuration
          </Button>
        </Space>
      </div>

      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Sidebar" key="sidebar">
            {renderTypeContent('sidebar')}
          </TabPane>
          <TabPane tab="Forms" key="forms">
            {renderTypeContent('forms')}
          </TabPane>
          <TabPane tab="Modal Dialogs" key="modal_dialog">
            {renderTypeContent('modal_dialog')}
          </TabPane>
          <TabPane tab="Dashboard" key="dashboard">
            {renderTypeContent('dashboard')}
          </TabPane>
          <TabPane tab="Feature Flags" key="feature_flags">
            <FeatureFlagsSettings />
          </TabPane>
        </Tabs>
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingConfig ? 'Edit Configuration' : 'Create Configuration'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false)
          setEditingConfig(null)
          form.resetFields()
        }}
        footer={null}
        width={720}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ isEnabled: true, isVisible: true, sortOrder: 0 }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="type"
                label="Configuration Type"
                rules={[{ required: true, message: 'Please select a type' }]}
              >
                <Select placeholder="Select type">
                  <Option value="sidebar">Sidebar</Option>
                  <Option value="forms">Forms</Option>
                  <Option value="modal_dialog">Modal Dialog</Option>
                  <Option value="dashboard">Dashboard</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="key"
                label="Unique Key"
                rules={[
                  { required: true, message: 'Please enter a unique key' },
                  { pattern: /^[a-z_]+$/, message: 'Key must be lowercase letters and underscores only' }
                ]}
              >
                <Input placeholder="e.g., data_sources" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="name"
            label="Display Name"
            rules={[{ required: true, message: 'Please enter a display name' }]}
          >
            <Input placeholder="e.g., Data Sources" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea placeholder="Describe what this configuration does" rows={2} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="icon"
                label="Icon"
              >
                <Input placeholder="e.g., DatabaseOutlined" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="route"
                label="Route"
              >
                <Input placeholder="e.g., /projects" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="sortOrder"
                label="Sort Order"
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="position"
                label="Position"
              >
                <Select placeholder="Select position" allowClear>
                  <Option value="top">Top</Option>
                  <Option value="middle">Middle</Option>
                  <Option value="bottom">Bottom</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="featureFlag"
                label="Feature Flag"
              >
                <Input placeholder="e.g., sidebar_data_sources" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="isEnabled"
                valuePropName="checked"
              >
                <Switch /> <span style={{ marginLeft: 8 }}>Enabled</span>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="isVisible"
                valuePropName="checked"
              >
                <Switch /> <span style={{ marginLeft: 8 }}>Visible</span>
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setIsModalVisible(false)}>
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={createMutation.isPending || updateMutation.isPending}
              >
                {editingConfig ? 'Update' : 'Create'} Configuration
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  )
}

export default AppConfigurationSettings